import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import Icon from '../components/Icon';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const loginAsDemo = async (email: string) => {
    setLoading(true);
    setError(null);
    try {
      await login(email, 'demo123');
      navigate('/');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Icon name="gavel" className="text-5xl text-primary-600" />
          <h2 className="mt-4 text-3xl font-bold text-gray-900">Sign in to your account</h2>
          <p className="mt-2 text-gray-600">
            Or{' '}
            <Link to="/register" className="text-primary-600 hover:text-primary-500">
              create a new account
            </Link>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card p-8 space-y-6">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <Icon name="email" size="small" className="text-gray-400" />
              Email address
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <Icon name="lock" size="small" className="text-gray-400" />
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <Icon name="error" size="small" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Icon name="progress_activity" size="small" className="animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                <Icon name="login" size="small" />
                Sign in
              </>
            )}
          </button>
        </form>

        <div className="card p-6">
          <p className="text-sm text-gray-600 mb-3 text-center flex items-center justify-center gap-1">
            <Icon name="flash_on" size="small" className="text-yellow-500" />
            Quick login as demo user:
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => loginAsDemo('alice@demo.auction')}
              className="btn-secondary text-sm flex items-center gap-1"
              disabled={loading}
            >
              <Icon name="person" size="small" className="text-pink-500" />
              Alice
            </button>
            <button
              onClick={() => loginAsDemo('bob@demo.auction')}
              className="btn-secondary text-sm flex items-center gap-1"
              disabled={loading}
            >
              <Icon name="person" size="small" className="text-blue-500" />
              Bob
            </button>
            <button
              onClick={() => loginAsDemo('charlie@demo.auction')}
              className="btn-secondary text-sm flex items-center gap-1"
              disabled={loading}
            >
              <Icon name="person" size="small" className="text-green-500" />
              Charlie
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
